import {
  type ParsedTimeline,
  type TimelineEvent,
  type TimelinePeriod,
  type TimelineSection,
} from './timeline-types';
import { DEFAULT_TIMELINE_SECTION_LABEL } from './timeline-constants';

const TIMELINE_HEADER_REGEX = /^timeline\b/i;
const TITLE_REGEX = /^title\s+(.+)$/i;
const SECTION_REGEX = /^section\s+(.+)$/i;

interface TimelineParseState {
  title: string | null;
  sections: TimelineSection[];
  periods: TimelinePeriod[];
  activeSectionIndex: number;
  lastPeriodIndex: number | null;
}

interface ActiveSectionResult {
  state: TimelineParseState;
  sectionIndex: number;
}

interface TimelineLineInput {
  state: TimelineParseState;
  line: string;
}

type TimelineLineHandler = (input: TimelineLineInput) => TimelineParseState | null;

function sanitizeMermaidLine(line: string): string {
  const commentIndex = line.indexOf('%%');
  const withoutComment = commentIndex >= 0 ? line.slice(0, commentIndex) : line;
  return withoutComment.replace(/;\s*$/, '').replace(/\s+$/, '');
}

function normalizeLabel(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim();
}

function getEventTextsFromSegment(segment: string): string[] {
  return segment
    .split(':')
    .map((token) => normalizeLabel(token))
    .filter((token) => token.length > 0);
}

function createTimelineParseState(): TimelineParseState {
  return {
    title: null,
    sections: [],
    periods: [],
    activeSectionIndex: -1,
    lastPeriodIndex: null,
  };
}

function addTimelineSection(input: {
  state: TimelineParseState;
  label: string;
}): TimelineParseState {
  const sectionIndex = input.state.sections.length;
  const nextSection: TimelineSection = {
    index: sectionIndex,
    label: input.label,
    periodStartIndex: input.state.periods.length,
    periodCount: 0,
  };

  return {
    ...input.state,
    sections: [...input.state.sections, nextSection],
    activeSectionIndex: sectionIndex,
  };
}

function ensureActiveSection(input: {
  state: TimelineParseState;
}): ActiveSectionResult {
  if (input.state.activeSectionIndex >= 0) {
    return {
      state: input.state,
      sectionIndex: input.state.activeSectionIndex,
    };
  }

  const state = addTimelineSection({
    state: input.state,
    label: DEFAULT_TIMELINE_SECTION_LABEL,
  });

  return {
    state,
    sectionIndex: state.activeSectionIndex,
  };
}

function updateTimelinePeriod(input: {
  state: TimelineParseState;
  periodIndex: number;
  period: TimelinePeriod;
}): TimelineParseState {
  return {
    ...input.state,
    periods: input.state.periods.map((period, periodIndex) =>
      periodIndex === input.periodIndex ? input.period : period,
    ),
  };
}

function appendContinuationEvents(input: {
  state: TimelineParseState;
  line: string;
}): TimelineParseState {
  if (input.state.lastPeriodIndex == null) {
    return input.state;
  }

  const targetPeriod = input.state.periods[input.state.lastPeriodIndex];
  if (!targetPeriod) {
    return input.state;
  }

  const continuationEvents = getEventTextsFromSegment(input.line);
  if (continuationEvents.length === 0) {
    return input.state;
  }

  const events = [
    ...targetPeriod.events,
    ...continuationEvents.map<TimelineEvent>((eventText, eventIndex) => ({
      text: eventText,
      periodIndex: targetPeriod.globalIndex,
      eventIndex: targetPeriod.events.length + eventIndex,
    })),
  ];

  return updateTimelinePeriod({
    state: input.state,
    periodIndex: input.state.lastPeriodIndex,
    period: {
      ...targetPeriod,
      events,
    },
  });
}

function parseTimelineSectionLine(input: {
  state: TimelineParseState;
  label: string;
}): TimelineParseState {
  const sectionLabel = normalizeLabel(input.label);
  if (!sectionLabel) {
    return input.state;
  }

  return {
    ...addTimelineSection({
      state: input.state,
      label: sectionLabel,
    }),
    lastPeriodIndex: null,
  };
}

function incrementSectionPeriodCount(input: {
  state: TimelineParseState;
  sectionIndex: number;
}): TimelineSection[] {
  return input.state.sections.map((section) =>
    section.index === input.sectionIndex
      ? {
          ...section,
          periodCount: section.periodCount + 1,
        }
      : section,
  );
}

function parseTimelinePeriodLine(input: TimelineLineInput): TimelineParseState | null {
  const separatorIndex = input.line.indexOf(':');
  if (separatorIndex <= 0) {
    return null;
  }

  const periodLabel = normalizeLabel(input.line.slice(0, separatorIndex));
  if (!periodLabel) {
    return null;
  }

  const eventSegment = input.line.slice(separatorIndex + 1);
  const eventTexts = getEventTextsFromSegment(eventSegment);
  if (eventTexts.length === 0) {
    return null;
  }

  const activeSection = ensureActiveSection({ state: input.state });
  const globalIndex = activeSection.state.periods.length;
  const events = eventTexts.map<TimelineEvent>((eventText, eventIndex) => ({
    text: eventText,
    periodIndex: globalIndex,
    eventIndex,
  }));
  const period: TimelinePeriod = {
    label: periodLabel,
    events,
    sectionIndex: activeSection.sectionIndex,
    globalIndex,
  };

  const stateWithPeriod = {
    ...activeSection.state,
    periods: [...activeSection.state.periods, period],
  };

  return {
    ...stateWithPeriod,
    sections: incrementSectionPeriodCount({
      state: stateWithPeriod,
      sectionIndex: activeSection.sectionIndex,
    }),
    lastPeriodIndex: globalIndex,
  };
}

function parseTimelineTitleLine(input: TimelineLineInput): TimelineParseState | null {
  const titleMatch = input.line.match(TITLE_REGEX);
  if (!titleMatch) {
    return null;
  }

  return {
    ...input.state,
    title: normalizeLabel(titleMatch[1] ?? '') || null,
  };
}

function parseTimelineSectionDirective(
  input: TimelineLineInput,
): TimelineParseState | null {
  const sectionMatch = input.line.match(SECTION_REGEX);
  if (!sectionMatch) {
    return null;
  }

  return parseTimelineSectionLine({
    state: input.state,
    label: sectionMatch[1] ?? '',
  });
}

function parseTimelineContinuationLine(
  input: TimelineLineInput,
): TimelineParseState | null {
  if (!input.line.startsWith(':')) {
    return null;
  }

  return appendContinuationEvents({
    state: input.state,
    line: input.line,
  });
}

const TIMELINE_LINE_HANDLERS: TimelineLineHandler[] = [
  parseTimelineTitleLine,
  parseTimelineSectionDirective,
  parseTimelineContinuationLine,
  parseTimelinePeriodLine,
];

function applyTimelineLineHandlers(input: TimelineLineInput): TimelineParseState {
  const handledState = TIMELINE_LINE_HANDLERS.reduce<TimelineParseState | null>(
    (nextState, handler) => nextState ?? handler(input),
    null,
  );

  return handledState ?? input.state;
}

function parseTimelineLine(input: TimelineLineInput): TimelineParseState {
  const line = input.line.trim();
  if (!line) {
    return input.state;
  }

  return applyTimelineLineHandlers({
    state: input.state,
    line,
  });
}

export function parseMermaidTimeline(raw: string): ParsedTimeline | null {
  const lines = raw
    .split('\n')
    .map((line) => sanitizeMermaidLine(line))
    .filter((line) => line.trim().length > 0);
  const headerIndex = lines.findIndex((line) => TIMELINE_HEADER_REGEX.test(line.trim()));
  if (headerIndex < 0) {
    return null;
  }

  const parsedState = lines
    .slice(headerIndex + 1)
    .reduce<TimelineParseState>(
      (state, line) => parseTimelineLine({ state, line }),
      createTimelineParseState(),
    );

  if (parsedState.periods.length === 0) {
    return null;
  }

  const nonEmptySections = parsedState.sections.filter(
    (section) => section.periodCount > 0,
  );

  return {
    title: parsedState.title,
    sections: nonEmptySections,
    periods: parsedState.periods,
  };
}
